import Tabs from "@/components/Tabs";
import useDashboardGreeting from "@/hooks/useDashboardGreeting";
import { useFlagsLoaded } from "@/hooks/useFeatureFlags";
import { useShowRosterDashboardTab } from "@/features/roster-activity";
import { Path, useNavigate } from "@/router";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import DashboardTabIcon from "./_components/DashboardTabIcon";
import CareerServicesDashboardSectionProvider from "./_components/CareerServicesDashboardSectionProvider";
import useIsCareerServicesDashboardEnabled from "./_hooks/useIsCareerServicesDashboardEnabled";
import {
  HOME_DASHBOARD_ASSOCIATES_PATH,
  HOME_DASHBOARD_JOBS_PATH,
  HOME_DASHBOARD_MEMBERS_PATH,
  HOME_DASHBOARD_OVERVIEW_PATH,
  SECTION_ROSTER_DASHBOARD_PATH
} from "./constants";

/**
 * Home dashboard tab bar: icon Home tab, career-work tabs, and optional Roster tab.
 *
 * Design reference: https://le-section-dashboard-prototype.m360.dev.pgahq.com/home/roster
 */
function normalizePathname(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

const CAREER_SERVICES_DASHBOARD_PATHS = new Set<string>([
  HOME_DASHBOARD_JOBS_PATH,
  HOME_DASHBOARD_MEMBERS_PATH,
  HOME_DASHBOARD_ASSOCIATES_PATH
]);

export default function HomeDashboardTabs() {
  const location = useLocation();
  const navigate = useNavigate();
  const greeting = useDashboardGreeting();
  const flagsLoaded = useFlagsLoaded();
  const showCareerServicesDashboard = useIsCareerServicesDashboardEnabled();
  const showRosterTab = useShowRosterDashboardTab();
  const currentPath = normalizePathname(location.pathname);

  const homeActive = currentPath === HOME_DASHBOARD_OVERVIEW_PATH;
  const jobsActive = currentPath === HOME_DASHBOARD_JOBS_PATH;
  const membersActive = currentPath === HOME_DASHBOARD_MEMBERS_PATH;
  const associatesActive = currentPath === HOME_DASHBOARD_ASSOCIATES_PATH;
  const rosterActive = currentPath.startsWith(SECTION_ROSTER_DASHBOARD_PATH);
  const showCareerServicesDashboardSectionFilter =
    showCareerServicesDashboard && CAREER_SERVICES_DASHBOARD_PATHS.has(currentPath);
  const preservedSearch = location.search;

  if (flagsLoaded && !showCareerServicesDashboard && CAREER_SERVICES_DASHBOARD_PATHS.has(currentPath)) {
    return <Navigate to={`${HOME_DASHBOARD_OVERVIEW_PATH}${preservedSearch}`} replace />;
  }

  const tabItems = [
    {
      label: "Home",
      iconOnly: true,
      icon: <DashboardTabIcon materialIcon={greeting.tabIcon} />,
      active: homeActive,
      testId: "tab-home",
      onClick: () => navigate(`${HOME_DASHBOARD_OVERVIEW_PATH}${preservedSearch}` as Path)
    },
    ...(showCareerServicesDashboard
      ? [
          {
            label: "Jobs",
            active: jobsActive,
            testId: "tab-jobs",
            onClick: () => navigate(`${HOME_DASHBOARD_JOBS_PATH}${preservedSearch}` as Path)
          },
          {
            label: "Members",
            active: membersActive,
            testId: "tab-members",
            onClick: () => navigate(`${HOME_DASHBOARD_MEMBERS_PATH}${preservedSearch}` as Path)
          },
          {
            label: "Associates",
            active: associatesActive,
            testId: "tab-associates",
            onClick: () => navigate(`${HOME_DASHBOARD_ASSOCIATES_PATH}${preservedSearch}` as Path)
          }
        ]
      : []),
    ...(showRosterTab
      ? [
          {
            label: "Roster",
            active: rosterActive,
            testId: "tab-roster",
            onClick: () => navigate(`${SECTION_ROSTER_DASHBOARD_PATH}${preservedSearch}` as Path)
          }
        ]
      : [])
  ];

  return (
    <div data-testid="home-dashboard-tabs">
      <Tabs tabItems={tabItems} />
      {showCareerServicesDashboardSectionFilter ? (
        <CareerServicesDashboardSectionProvider>
          <div className="mt-12">
            <Outlet />
          </div>
        </CareerServicesDashboardSectionProvider>
      ) : (
        <Outlet />
      )}
    </div>
  );
}
